/**
 * @fileoverview Login dialog modal for landing page.
 * Opens as a modal when user clicks "Log in" in navbar.
 * @module features/landing/components/LoginDialog
 */

import { ReactElement, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authService } from '@/lib/authService'
import { supabase } from '@/lib/supabaseClient'

interface LoginDialogProps {
  /** Trigger element that opens the dialog */
  children: React.ReactNode
}

/**
 * Login dialog modal component.
 * Provides email/password authentication in a modal overlay.
 *
 * @component
 */
export function LoginDialog({ children }: LoginDialogProps): ReactElement {
  const { t } = useTranslation('features-auth')
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setLoading(true)
    setError('')

    try {
      await authService.login(email, password)
      setOpen(false)
      navigate('/dashboard')
    } catch (err: unknown) {
      console.error('Login error:', err)
      const anyErr = err as { message?: string }
      setError(anyErr?.message || t('loginForm.errorFallback'))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault()
    // TODO: Implement forgot password functionality
    console.debug('Forgot password clicked')
  }

  const handleGoogleLogin = async (): Promise<void> => {
    setError('')
    const redirectBase = (import.meta.env.VITE_PUBLIC_URL as string) || window.location.origin
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${redirectBase}/`,
      },
    })
    if (oauthError) {
      setError(oauthError.message)
    }
  }

  const handleOpenChange = (isOpen: boolean): void => {
    setOpen(isOpen)
    // Reset form state when closing
    if (!isOpen) {
      setEmail('')
      setPassword('')
      setError('')
      setShowPassword(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-sm" showCloseButton={false}>
        <DialogHeader className="flex-row items-center justify-between">
          <img
            src="/abyssblock-white.webp"
            alt="AbyssBlock"
            className="h-7"
          />
          <DialogTitle className="sr-only">Log in</DialogTitle>
          <DialogClose className="rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-4">
          {error && (
            <div className="p-3 text-sm text-destructive-foreground bg-destructive rounded-lg">{error}</div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="login-email">E-mail</Label>
            <Input
              id="login-email"
              name="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="login-password">{t('loginForm.passwordLabel')}</Label>
              <a
                href="#"
                onClick={handleForgotPassword}
                className="ml-auto text-xs underline-offset-4 hover:underline text-muted-foreground"
              >
                {t('loginForm.forgotPassword')}
              </a>
            </div>
            <div className="relative">
              <Input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('loginForm.submitting') : t('loginForm.submit')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <img src="/google-icon.png" alt="" className="size-4" />
              {t('loginForm.loginWithGoogle')}
            </Button>
          </div>

        </form>
      </DialogContent>
    </Dialog>
  )
}
